import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid, Card, CardContent,
  CardActions, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, LinearProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Tooltip, Switch, FormControlLabel,
} from '@mui/material';
import { HealthAndSafety, Add, Edit, Delete, CheckCircle, Close, People, Block } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const CAT_CFG: Record<string, { emoji: string; label: string; color: string }> = {
  health:     { emoji: '🏥', label: 'Health',     color: '#c62828' },
  dental:     { emoji: '🦷', label: 'Dental',     color: '#1565c0' },
  vision:     { emoji: '👓', label: 'Vision',     color: '#1b5e20' },
  life:       { emoji: '❤️', label: 'Life',       color: '#880e4f' },
  retirement: { emoji: '🏖️', label: 'Retirement', color: '#e65100' },
  allowance:  { emoji: '💸', label: 'Allowance',  color: '#4a148c' },
  wellness:   { emoji: '💪', label: 'Wellness',   color: '#006064' },
  transport:  { emoji: '🚇', label: 'Transport',  color: '#37474f' },
  meal:       { emoji: '🍱', label: 'Meal',       color: '#bf360c' },
  education:  { emoji: '🎓', label: 'Education',  color: '#1a237e' },
  other:      { emoji: '📋', label: 'Other',      color: '#546e7a' },
};

const STATUS_CFG: Record<string, { color: any; label: string }> = {
  active:   { color: 'success', label: 'Enrolled' },
  pending:  { color: 'warning', label: 'Pending' },
  inactive: { color: 'default', label: 'Inactive' },
  waived:   { color: 'error',   label: 'Waived' },
};

function fmt(n: number | null | undefined) {
  if (!n) return '—';
  return `HK$${Number(n).toLocaleString('en-HK')}`;
}

interface Plan { id: string; name: string; category: string; provider?: string; description?: string; isActive: boolean; monthlyCost?: number; employeeContribution?: number; coverageLimit?: number; eligibility?: string; myEnrollment?: { id: string; status: string; enrolledDate?: string; membershipNumber?: string } | null; }
interface Enrollment { id: string; planId: string; employeeId: string; status: string; enrolledDate?: string; effectiveDate?: string; membershipNumber?: string; plan?: Plan; employee?: { firstName: string; lastName: string }; }

function PlanCard({ plan, onEnroll, onWaive, onEdit, onDelete, onViewEnrollments, isHR }: any) {
  const cat = CAT_CFG[plan.category] ?? CAT_CFG.other;
  const enrolled = plan.myEnrollment?.status === 'active';

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', border: enrolled ? `2px solid ${cat.color}` : '1px solid #e0e0e0' }}>
      <CardContent sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography fontSize={24}>{cat.emoji}</Typography>
            <Box>
              <Typography fontWeight={700} variant="body1">{plan.name}</Typography>
              {plan.provider && <Typography variant="caption" color="text.secondary">{plan.provider}</Typography>}
            </Box>
          </Box>
          {enrolled && <Chip icon={<CheckCircle />} label="Enrolled" size="small" color="success" />}
        </Box>
        {plan.description && <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{plan.description}</Typography>}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
          {plan.monthlyCost > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">Company Pays</Typography>
              <Typography variant="body2" fontWeight={700}>{fmt(plan.monthlyCost)}/mo</Typography>
            </Box>
          )}
          {plan.employeeContribution > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">Your Contribution</Typography>
              <Typography variant="body2" fontWeight={700}>{fmt(plan.employeeContribution)}/mo</Typography>
            </Box>
          )}
          {plan.coverageLimit && (
            <Box>
              <Typography variant="caption" color="text.secondary">Coverage</Typography>
              <Typography variant="body2" fontWeight={700}>{fmt(plan.coverageLimit)}</Typography>
            </Box>
          )}
        </Box>
        {plan.eligibility && plan.eligibility !== 'all' && (
          <Chip label={`Eligibility: ${plan.eligibility.replace('_', ' ')}`} size="small" variant="outlined" sx={{ mt: 1 }} />
        )}
        {plan.myEnrollment?.membershipNumber && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Member #: {plan.myEnrollment.membershipNumber}
          </Typography>
        )}
      </CardContent>
      <CardActions sx={{ pt: 0, gap: 0.5 }}>
        {!enrolled && !plan.myEnrollment && (
          <Button size="small" variant="contained" onClick={() => onEnroll(plan.id)} sx={{ bgcolor: cat.color }}>Enroll</Button>
        )}
        {enrolled && (
          <Button size="small" color="error" startIcon={<Block />} onClick={() => onWaive(plan.myEnrollment.id)}>Waive</Button>
        )}
        {plan.myEnrollment && !enrolled && (
          <Chip label={STATUS_CFG[plan.myEnrollment.status]?.label ?? plan.myEnrollment.status} size="small" color={STATUS_CFG[plan.myEnrollment.status]?.color} />
        )}
        {isHR && (
          <>
            <Tooltip title="Enrollments"><IconButton size="small" onClick={() => onViewEnrollments(plan)}><People fontSize="small" /></IconButton></Tooltip>
            <IconButton size="small" onClick={() => onEdit(plan)}><Edit fontSize="small" /></IconButton>
            <IconButton size="small" color="error" onClick={() => onDelete(plan.id)}><Delete fontSize="small" /></IconButton>
          </>
        )}
      </CardActions>
    </Card>
  );
}

export default function Benefits() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes(user?.role ?? '');

  const [tab, setTab] = useState(0);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [enrollDialog, setEnrollDialog] = useState<string | null>(null); // planId
  const [editDialog, setEditDialog] = useState<Partial<Plan> | null>(null);
  const [enrollmentsDialog, setEnrollmentsDialog] = useState<{ plan: Plan; data: Enrollment[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [catFilter, setCatFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [dash, st] = await Promise.all([
        apiFetch('/api/benefits/my-dashboard'),
        apiFetch('/api/benefits/stats'),
      ]);
      setPlans(dash || []); setStats(st);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const enroll = async (planId: string) => {
    try { await apiFetch('/api/benefits/enroll', { method: 'POST', body: JSON.stringify({ planId }) }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const waive = async (enrollmentId: string) => {
    if (!window.confirm('Waive this benefit?')) return;
    try { await apiFetch(`/api/benefits/enrollments/${enrollmentId}/waive`, { method: 'PATCH' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const save = async () => {
    if (!editDialog) return;
    try {
      editDialog.id
        ? await apiFetch(`/api/benefits/plans/${editDialog.id}`, { method: 'PATCH', body: JSON.stringify(editDialog) })
        : await apiFetch('/api/benefits/plans', { method: 'POST', body: JSON.stringify(editDialog) });
      setEditDialog(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const deletePlan = async (id: string) => {
    if (!window.confirm('Delete this plan?')) return;
    try { await apiFetch(`/api/benefits/plans/${id}`, { method: 'DELETE' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const viewEnrollments = async (plan: Plan) => {
    const data = await apiFetch(`/api/benefits/plans/${plan.id}/enrollments`).catch(() => []);
    setEnrollmentsDialog({ plan, data });
  };

  const filtered = catFilter ? plans.filter(p => p.category === catFilter) : plans;
  const enrolled = plans.filter(p => p.myEnrollment?.status === 'active');

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HealthAndSafety sx={{ fontSize: 32, color: '#c62828' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Benefits</Typography>
            <Typography variant="body2" color="text.secondary">Manage your company benefits & enrollments</Typography>
          </Box>
        </Box>
        {isHR && (
          <Button variant="contained" startIcon={<Add />} sx={{ bgcolor: '#1a237e' }}
            onClick={() => setEditDialog({ isActive: true, category: 'health', eligibility: 'all' })}>
            Add Plan
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="h4" fontWeight={700} color="#c62828">{stats.activePlans}</Typography>
                <Typography variant="caption" color="text.secondary">Active Plans</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="h4" fontWeight={700} color="#2e7d32">{enrolled.length}</Typography>
                <Typography variant="caption" color="text.secondary">Your Enrollments</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="h4" fontWeight={700} color="#1a237e">{stats.totalEnrollments}</Typography>
                <Typography variant="caption" color="text.secondary">Total Enrollments</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="h4" fontWeight={700} color="#e65100">{fmt(stats.totalMonthlyCost)}</Typography>
                <Typography variant="caption" color="text.secondary">Monthly Spend</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Category filter */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Chip label="All" onClick={() => setCatFilter('')} color={!catFilter ? 'primary' : 'default'} variant={!catFilter ? 'filled' : 'outlined'} />
        {Object.entries(CAT_CFG).map(([k, v]) => (
          <Chip key={k} label={`${v.emoji} ${v.label}`} onClick={() => setCatFilter(k)}
            color={catFilter === k ? 'primary' : 'default'} variant={catFilter === k ? 'filled' : 'outlined'} />
        ))}
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={2}>
        {filtered.map(p => (
          <Grid key={p.id} size={{ xs: 12, sm: 6, md: 4 }}>
            <PlanCard plan={p} onEnroll={enroll} onWaive={waive}
              onEdit={setEditDialog} onDelete={deletePlan}
              onViewEnrollments={viewEnrollments} isHR={isHR} />
          </Grid>
        ))}
        {filtered.length === 0 && !loading && (
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>No benefit plans available.</Paper>
          </Grid>
        )}
      </Grid>

      {/* ── EDIT PLAN DIALOG ── */}
      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editDialog?.id ? 'Edit Plan' : 'New Benefit Plan'}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setEditDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Plan Name" fullWidth required value={editDialog?.name ?? ''}
              onChange={e => setEditDialog(p => ({ ...p, name: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Category" select fullWidth value={editDialog?.category ?? 'health'}
                onChange={e => setEditDialog(p => ({ ...p, category: e.target.value }))}>
                {Object.entries(CAT_CFG).map(([v, c]) => <MenuItem key={v} value={v}>{c.emoji} {c.label}</MenuItem>)}
              </TextField>
              <TextField label="Provider" fullWidth value={editDialog?.provider ?? ''}
                onChange={e => setEditDialog(p => ({ ...p, provider: e.target.value }))} />
            </Box>
            <TextField label="Description" fullWidth multiline rows={2} value={editDialog?.description ?? ''}
              onChange={e => setEditDialog(p => ({ ...p, description: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Monthly Cost (HKD)" type="number" fullWidth value={editDialog?.monthlyCost ?? ''}
                onChange={e => setEditDialog(p => ({ ...p, monthlyCost: Number(e.target.value) }))} />
              <TextField label="Employee Contribution" type="number" fullWidth value={editDialog?.employeeContribution ?? ''}
                onChange={e => setEditDialog(p => ({ ...p, employeeContribution: Number(e.target.value) }))} />
            </Box>
            <TextField label="Coverage Limit (HKD)" type="number" fullWidth value={editDialog?.coverageLimit ?? ''}
              onChange={e => setEditDialog(p => ({ ...p, coverageLimit: Number(e.target.value) }))} />
            <TextField label="Eligibility" select fullWidth value={editDialog?.eligibility ?? 'all'}
              onChange={e => setEditDialog(p => ({ ...p, eligibility: e.target.value }))}>
              <MenuItem value="all">All Employees</MenuItem>
              <MenuItem value="full_time">Full-Time Only</MenuItem>
              <MenuItem value="confirmed">Confirmed (Post-Probation)</MenuItem>
            </TextField>
            <FormControlLabel control={<Switch checked={editDialog?.isActive ?? true}
              onChange={e => setEditDialog(p => ({ ...p, isActive: e.target.checked }))} />}
              label="Active" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!editDialog?.name}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ── ENROLLMENTS DIALOG ── */}
      <Dialog open={!!enrollmentsDialog} onClose={() => setEnrollmentsDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Enrollments — {enrollmentsDialog?.plan.name}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setEnrollmentsDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><b>Employee</b></TableCell>
                  <TableCell><b>Enrolled</b></TableCell>
                  <TableCell><b>Member #</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(enrollmentsDialog?.data ?? []).map(e => (
                  <TableRow key={e.id} hover>
                    <TableCell>{e.employee?.firstName} {e.employee?.lastName}</TableCell>
                    <TableCell>{e.enrolledDate ? new Date(e.enrolledDate + 'T00:00:00').toLocaleDateString('en-HK', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}</TableCell>
                    <TableCell>{e.membershipNumber ?? '—'}</TableCell>
                  </TableRow>
                ))}
                {(enrollmentsDialog?.data ?? []).length === 0 && (
                  <TableRow><TableCell colSpan={3} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>No active enrollments.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions><Button onClick={() => setEnrollmentsDialog(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
